import { ValidationStates } from '@opengov/component-library/dist';
import { IReportLocale } from '@opengov/reporting-app/application/common/locale';
import { EReportsListV4ItemType, IReportV4 } from '@opengov/reporting-types';
import React, { useEffect, useMemo } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';

import { reportEffects } from '../../../report/effects';
import { IReportFormHolder } from '../../../forms/reportForm/types';
import { computeDuplicateReportTitle } from '../DuplicateFoldersAndReportsDialog/utils/computeDuplicateReportTitle';
import {
  DuplicateFoldersAndReportsCustomDuplicateParams,
  DuplicateFoldersAndReportsDialog
} from '../DuplicateFoldersAndReportsDialog/DuplicateFoldersAndReportsDialog';
import { CopyReportFoldersProvider } from './CopyReportFoldersProvider';

export interface ICopyReportModalProps extends IReportFormHolder {
  newReport: any;
  reportLocaleText: IReportLocale;
  currentTemplate: any;
  currentReport: any;
  entityId?: number;
  sourceParentFolderId?: string;
  closeModal(): void;
  onCopy(
    copySavedViews: boolean,
    copyAccess: ValidationStates,
    navigate: NavigateFunction,
    parentFolderId?: string | null
  ): void;
}

type CopyReportModalContentProps = Omit<ICopyReportModalProps, 'entityId'>;

const buildCurrentReportAsSelectedItem = (
  currentReport: ICopyReportModalProps['currentReport'],
  sourceParentFolderId?: string
): IReportV4 =>
  ({
    id: `${currentReport?.id ?? ''}`,
    title: currentReport?.title ?? '',
    type: EReportsListV4ItemType.ACCOUNT_SUMMARY_REPORT,
    parentFolderId: sourceParentFolderId ?? currentReport?.parentFolderId ?? null
  }) as IReportV4;

const CopyReportModalContent = (props: Readonly<CopyReportModalContentProps>) => {
  const {
    closeModal,
    titleState,
    onReportDescriptionBlur,
    onReportDescriptionChange,
    onReportTitleBlur,
    onReportTitleChange,
    currentTemplate,
    currentReport,
    sourceParentFolderId
  } = props;
  const navigate = useNavigate();

  const defaultSourceTitle = useMemo<string>(
    () => (currentTemplate ? currentTemplate.standardReportName : (currentReport?.title ?? '')),
    [currentTemplate, currentReport?.title]
  );

  const initialTitle = useMemo(() => computeDuplicateReportTitle(defaultSourceTitle), [defaultSourceTitle]);

  // Prime the legacy report-create state machine on mount so title validation
  // (`titleState`) reflects the duplicate's pre-filled title. The dialog owns
  // its own local title state seeded from `initialTitle`; both stay in sync
  // via the `onTitleChange` callback below.
  useEffect(() => {
    onReportTitleChange(initialTitle);
  }, [initialTitle]);

  const selectedItems = useMemo<IReportV4[]>(
    () => [buildCurrentReportAsSelectedItem(currentReport, sourceParentFolderId)],
    [currentReport, sourceParentFolderId]
  );

  // The dialog closes itself (resetting the modal state) as soon as this
  // resolves, so we have to wait for the effect to finish. Resolving early
  // would tear down the "Creating report" modal the effect opens for the
  // duration of the copy.
  const handleCustomDuplicate = async ({ parentFolderId }: DuplicateFoldersAndReportsCustomDuplicateParams) => {
    if (currentTemplate) {
      await reportEffects.createCustomReportFromStandardAndRedirect({
        isDimensional: false,
        copySavedViews: true,
        copyAccess: ValidationStates.None,
        templateSavedViews: currentTemplate?.savedViews,
        parentFolderId
      });
      return;
    }
    await reportEffects.createCustomReportAndRedirect({ copySavedViews: true, navigate, parentFolderId });
  };

  return (
    <DuplicateFoldersAndReportsDialog
      selectedItems={selectedItems}
      onClose={closeModal}
      initialTitle={initialTitle}
      titleValidationState={titleState}
      onTitleChange={onReportTitleChange}
      onTitleBlur={onReportTitleBlur}
      onDescriptionChange={onReportDescriptionChange}
      onDescriptionBlur={onReportDescriptionBlur}
      onCustomDuplicate={handleCustomDuplicate}
    />
  );
};

export const CopyReportModal = ({ entityId, ...contentProps }: Readonly<ICopyReportModalProps>) => {
  return (
    <CopyReportFoldersProvider entityId={entityId ?? 0}>
      <CopyReportModalContent {...contentProps} />
    </CopyReportFoldersProvider>
  );
};
